import { Mail } from "lucide-react";

export default function PrivacyPolicyPage() {
    return (
        <div className="min-h-[70vh]" data-testid="privacy-policy-page">
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                    <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Legal</p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tight mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-muted-foreground text-sm">Last updated: June 2026</p>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20 max-w-3xl">
                <div className="space-y-8 text-muted-foreground text-sm leading-relaxed">
                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Introduction</h2>
                        <p>
                            Dry Ice Marketplace ("we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website dryicemarketplace.com and interact with our services.
                        </p>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Information We Collect</h2>
                        <p className="mb-3">We may collect the following types of information:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li><span className="text-foreground font-bold">Contact Information</span> — Name, email address, phone number, and company name provided through our quote request and guidance forms.</li>
                            <li><span className="text-foreground font-bold">Usage Data</span> — Information about how you interact with our website, including pages visited, time spent on pages, and referring websites.</li>
                            <li><span className="text-foreground font-bold">Cookies & Tracking</span> — We use cookies and similar technologies (including Google Analytics and Google Ads tracking) to improve your browsing experience and measure website performance.</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">How We Use Your Information</h2>
                        <p className="mb-3">We use your information solely for the following purposes:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>To respond to your quote requests and inquiries</li>
                            <li>To provide product recommendations and guidance</li>
                            <li>To process equipment orders and coordinate delivery</li>
                            <li>To improve our website and customer experience</li>
                            <li>To send relevant follow-up communications related to your inquiry</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">We Do Not Share Your Information</h2>
                        <p>
                            Your personal information will <span className="text-foreground font-bold">never be sold, rented, traded, or shared with any third party</span> for marketing or commercial purposes. We do not provide your contact details, inquiry data, or any personal information to outside companies, advertisers, or data brokers. Your information is used exclusively by Dry Ice Marketplace and our service partner Dry Ice Bulls to fulfill your requests and provide customer support.
                        </p>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Cookies</h2>
                        <p className="mb-3">
                            Our website uses cookies to enhance functionality and analyze site traffic. These include:
                        </p>
                        <ul className="list-disc list-inside space-y-1">
                            <li><span className="text-foreground font-bold">Essential Cookies</span> — Required for the website to function properly.</li>
                            <li><span className="text-foreground font-bold">Analytics Cookies</span> — Help us understand how visitors use our site (e.g., Google Analytics).</li>
                            <li><span className="text-foreground font-bold">Advertising Cookies</span> — Used by Google Ads to measure ad performance and conversions.</li>
                        </ul>
                        <p className="mt-3">You can manage or disable cookies through your browser settings at any time.</p>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">SMS & Phone Communications</h2>
                        <p className="mb-4">
                            Your phone number will be used exclusively to send you messages you have opted to receive based on your consent. We do not sell or share your personal information with third parties.
                        </p>
                        <p className="mb-4">
                            By providing your phone number you consent to receive text messages from Dry Ice Marketplace & Dry Ice Bulls regarding your inquiries, account updates, and other relevant information. Message frequency will vary. Message and data rates may apply.
                        </p>
                        <p className="mb-4">
                            For assistance, reply <span className="text-foreground font-bold">HELP</span> or contact <a href="mailto:sales@dryicemarketplace.com" className="text-primary hover:underline">sales@dryicemarketplace.com</a> / <a href="tel:9842010767" className="text-primary hover:underline">984-201-0767</a>
                        </p>
                        <p>
                            To stop receiving messages, reply <span className="text-foreground font-bold">STOP</span>. No further messages will be sent. For details, see our <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
                        </p>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Data Security</h2>
                        <p>
                            We take reasonable measures to protect your personal information from unauthorized access, alteration, or disclosure. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                        </p>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Third-Party Links</h2>
                        <p>
                            Our website may contain links to third-party websites (such as manufacturer sites or financing partners). We are not responsible for the privacy practices of these external sites and encourage you to review their privacy policies.
                        </p>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Your Rights</h2>
                        <p className="mb-3">You have the right to:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Request access to the personal information we hold about you</li>
                            <li>Request correction or deletion of your personal data</li>
                            <li>Opt out of any future communications</li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.
                        </p>
                    </div>

                    <div className="border-t border-border pt-6">
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Contact Us</h2>
                        <p className="mb-3">If you have any questions about this Privacy Policy or how your data is handled, please contact us:</p>
                        <a href="mailto:sales@dryicemarketplace.com" className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
                            <Mail className="h-4 w-4" />
                            sales@dryicemarketplace.com
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
